"use client"

import { useState, useEffect, useRef } from "react"
import { cn } from "@/lib/utils"
import { TagSelectionModal } from "./tag-selection-modal"

interface CombinedInfoTickerProps {
  title: string
  tags?: string[]
  onTagsSelected?: (includeTags: string[], excludeTags: string[]) => void
  className?: string
}

export function CombinedInfoTicker({ title, tags = [], onTagsSelected, className }: CombinedInfoTickerProps) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const tickerRef = useRef<HTMLDivElement>(null)
  const viewportRef = useRef<HTMLDivElement>(null)
  const [tickerWidth, setTickerWidth] = useState(0)
  // True when one copy of the text fits in the card: then it stays still and is shown once.
  // Scrolling a short label (an artist name on Doujins By Artists) left it half out of view.
  const [fits, setFits] = useState(false)
  const fitsRef = useRef(false)

  useEffect(() => {
    const track = tickerRef.current
    const viewport = viewportRef.current
    if (!track || !viewport) return
    const measure = () => {
      // the track holds the text twice while it scrolls, once while it is still
      const oneCopy = fitsRef.current ? track.scrollWidth : track.scrollWidth / 2
      setTickerWidth(oneCopy * 2)
      const nowFits = oneCopy <= viewport.clientWidth
      if (nowFits !== fitsRef.current) {
        fitsRef.current = nowFits
        setFits(nowFits)
      }
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(viewport)
    return () => observer.disconnect()
  }, [title, tags])

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent triggering parent click handlers
    if (tags.length > 0) {
      setIsModalOpen(true)
    }
  }

  const handleTagsSelected = (includeTags: string[], excludeTags: string[]) => {
    onTagsSelected?.(includeTags, excludeTags)
    setIsModalOpen(false)
  }

  // Combine title and tags with bullet separators
  const items = [title, ...tags.slice(0, 10)] // Show title + up to 10 tags
  const duplicatedItems = fits ? items : [...items, ...items] // Duplicate for seamless loop, only while it scrolls

  return (
    <>
      <button
        onClick={handleClick}
        className={cn(
          "relative overflow-hidden bg-secondary/50 transition-colors",
          "rounded px-2 py-1.5 w-full",
          "flex items-center",
          tags.length > 0 ? "hover:bg-secondary/70 cursor-pointer" : "cursor-default",
          className
        )}
      >
        <div ref={viewportRef} className="relative w-full overflow-hidden">
          <div
            ref={tickerRef}
            className={cn("flex gap-1 whitespace-nowrap", !fits && "animate-scroll-tags")}
            style={{
              animationDuration: `${Math.max(10, tickerWidth / 80)}s`,
            }}
          >
            {duplicatedItems.map((item, idx) => (
              <span
                key={`${item}-${idx}`}
                className={cn(
                  "inline-flex items-center text-xs",
                  idx % items.length === 0 ? "font-semibold text-foreground" : "font-medium text-foreground/70"
                )}
              >
                {item}
                {idx < duplicatedItems.length - 1 && (
                  <span className="mx-1.5 text-muted-foreground">•</span>
                )}
              </span>
            ))}
          </div>
        </div>
      </button>

      {tags.length > 0 && (
        <TagSelectionModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          tags={tags}
          onConfirm={handleTagsSelected}
        />
      )}
    </>
  )
}
